import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../utils/Url";
import { Header } from "../header/Header";
import { Sidebar } from "../sidebar/Sidebar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

export const UserQueries = () => {
  const [queries, setQueries] = useState([]);
  const [filteredQueries, setFilteredQueries] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // 1. React Router SearchParams hook setup
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // 2. Extract current page from URL (Default: 1)
  const currentPage = Number(searchParams.get("page")) || 1;

  // 3. Pagination limits setup
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredQueries.length / itemsPerPage);
  const lastIndex = currentPage * itemsPerPage;
  const firstIndex = lastIndex - itemsPerPage;
  const currentQueries = filteredQueries.slice(firstIndex, lastIndex);

  // 4. Update page number in URL
  const handlePageChange = (newPage) => {
    setSearchParams((prevParams) => {
      prevParams.set("page", newPage);
      return prevParams;
    });
  };
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Search type karte hi page ko 1 par reset karo taaki empty state ya index out-of-bound na ho
    setSearchParams((prevParams) => {
      prevParams.set("page", 1);
      return prevParams;
    });
  };
  const getQueries = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem("token");
      const { data } = await axios.get(`${API_URL}/api/contact/get-contact`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (data) {
        setQueries(data?.data || []);
        setFilteredQueries(data?.data || []);
      }
    } catch (error) {
      console.error("Fetch Queries Error:", error);
      toast.error("Failed to sync contact log registry");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getQueries();
  }, []);

  /* 🔍 Instant Local Search Filter */
  useEffect(() => {
    const query = searchQuery.toLowerCase().trim();
    let filtered = queries;

    if (query !== "") {
      filtered = queries.filter(
        (q) =>
          q.name?.toLowerCase().includes(query) ||
          q.email?.toLowerCase().includes(query) ||
          q.mobile?.includes(query),
      );
    }

    setFilteredQueries(filtered);

    // Guard Clause: Agar current page max totalPages se bada ho jaye search ke baad,
    // toh safe page number par redirect karo
    const maxPages = Math.ceil(filtered.length / itemsPerPage) || 1;
    if (currentPage > maxPages) {
      setSearchParams((prevParams) => {
        prevParams.set("page", maxPages);
        return prevParams;
      });
    }
  }, [searchQuery, queries, currentPage]);

  const deleteQuery = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to permanently purge this user query?",
    );

    if (!confirmDelete) return;

    // Optimistic UI update
    const originalQueries = queries;
    setQueries((prev) => prev.filter((q) => q._id !== id));

    try {
      const token = sessionStorage.getItem("token");
      await axios.delete(`${API_URL}/api/admin/delete/ques/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Query log cleared successfully 🗑️");
    } catch (error) {
      // Rollback on failure
      setQueries(originalQueries);
      console.error("Delete Error:", error);
      toast.error("Failed to eliminate query entry");
    }
  };

  return (
    <>
      <Header />
      <Sidebar />

      <div
        className="mt-5 main-content px-3 px-md-4 py-4"
        style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}
      >
        {/* PREMIUM MODULE BOARD HEADER */}
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 mb-4 p-3 bg-white border rounded-4 shadow-sm">
          <div>
            <h2 className="fw-bold text-gray-800 mb-0 fs-3">
              Customer Desk Queries
            </h2>
            <p className="text-muted small mb-0">
              Manage incoming contact forms, support tickets, and business
              requests
            </p>
          </div>
          <div className="bg-primary-subtle px-3 py-2 rounded-3 border border-primary-subtle">
            <span className="text-primary fw-semibold me-1">
              Active Tickets:
            </span>
            <span className="fw-bold text-primary fs-5">
              {filteredQueries.length}
            </span>
          </div>
        </div>

        {/* INTERACTIVE FILTRATION BAR */}
        <div className="card border-0 rounded-4 shadow-sm mb-4">
          <div className="card-body p-3">
            <div className="input-group">
              <span className="input-group-text bg-light border-light-subtle text-muted">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control rounded-end-3 border-light-subtle bg-light-subtle py-2"
                placeholder="Search queries by name, email address, or mobile string..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* DATA PRESENTATION MATRIX */}
        <div className="card border-0 rounded-4 shadow-sm overflow-hidden">
          <div className="card-body p-0">
            {loading ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <div
                  className="spinner-border text-primary mb-3"
                  role="status"
                ></div>
                <span className="text-muted small fw-medium">
                  Parsing helpdesk data logs...
                </span>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0 custom-admin-table">
                  <thead className="bg-light border-bottom">
                    <tr>
                      <th
                        className="text-muted text-uppercase fs-7 fw-bold px-4 py-3"
                        style={{ width: "60px" }}
                      >
                        #
                      </th>
                      <th className="text-muted text-uppercase fs-7 fw-bold py-3">
                        User Details
                      </th>
                      <th className="text-muted text-uppercase fs-7 fw-bold py-3">
                        Communication Channel
                      </th>
                      <th
                        className="text-muted text-uppercase fs-7 fw-bold py-3"
                        style={{ maxWidth: "400px" }}
                      >
                        Message/Core Intent
                      </th>
                      <th className="text-muted text-uppercase fs-7 fw-bold py-3">
                        Logged Date
                      </th>
                      <th className="text-muted text-uppercase fs-7 fw-bold px-4 py-3 text-end">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody className="border-0">
                    {currentQueries.length > 0 ? (
                      currentQueries.map((ques, index) => (
                        <tr
                          key={ques?._id}
                          className="align-middle transition-all"
                        >
                          {/* Index Counter */}
                          <td className="px-4 fw-semibold text-muted">
                            {firstIndex + index + 1}
                          </td>

                          {/* Name Context */}
                          <td>
                            <div
                              className="fw-bold text-dark"
                              style={{ fontSize: "14.5px" }}
                            >
                              {ques?.name || "Anonymous Sender"}
                            </div>
                          </td>

                          {/* Contact Info Channels */}
                          <td>
                            <div
                              className="text-dark fw-medium"
                              style={{ fontSize: "13.5px" }}
                            >
                              <i className="bi bi-envelope text-muted me-1.5"></i>
                              {ques?.email}
                            </div>
                            {ques?.mobile && (
                              <div className="text-muted small mt-0.5">
                                <i className="bi bi-telephone text-muted me-1.5"></i>
                                {ques?.mobile}
                              </div>
                            )}
                          </td>

                          {/* Request Message Box */}
                          <td style={{ maxWidth: "400px" }}>
                            <div
                              className="bg-light p-2.5 rounded-3 border border-light-subtle text-secondary text-wrap font-sans"
                              style={{ fontSize: "13px", lineHeight: "1.45" }}
                            >
                              {ques?.message || (
                                <span className="text-muted italic">
                                  No text content provided.
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Timestamp Block */}
                          <td className="text-muted fs-7">
                            {ques?.createdAt ? (
                              <>
                                <div className="text-dark fw-medium">
                                  {new Date(ques.createdAt).toLocaleDateString(
                                    "en-IN",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    },
                                  )}
                                </div>
                                <div
                                  className="text-muted small"
                                  style={{ fontSize: "11px" }}
                                >
                                  {new Date(ques.createdAt).toLocaleTimeString(
                                    "en-IN",
                                    {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    },
                                  )}
                                </div>
                              </>
                            ) : (
                              "—"
                            )}
                          </td>

                          {/* Administrative Purge Actions */}
                          <td className="px-4 text-end">
                            <button
                              onClick={() => deleteQuery(ques?._id)}
                              className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 fw-medium px-2.5 shadow-xs"
                              title="Resolve and delete ticket"
                            >
                              <i className="bi bi-check2-circle"></i> Resolve
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <div className="text-muted d-flex flex-column align-items-center justify-content-center gap-2">
                            <i className="bi bi-chat-left-heart fs-1 text-success"></i>
                            <p className="mb-0 fw-semibold text-dark fs-5">
                              Inbox is entirely cleared!
                            </p>
                            <p className="mb-0 text-muted small">
                              No client-side inquiries matching filter indices.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PAGINATION CONTROL BAR */}
      {!loading && totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center p-4 border-top bg-light-subtle">
          <div className="text-muted small">
            Showing entries{" "}
            <span className="fw-semibold text-dark">
              {filteredQueries.length > 0 ? firstIndex + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="fw-semibold text-dark">
              {lastIndex > filteredQueries.length
                ? filteredQueries.length
                : lastIndex}
            </span>{" "}
            of{" "}
            <span className="fw-semibold text-dark">
              {filteredQueries.length}
            </span>{" "}
            records
          </div>
          <nav aria-label="Queries pagination navigation">
            <ul className="pagination pagination-sm mb-0 gap-1">
              {/* First Page Button */}
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link rounded-2 border px-3"
                  onClick={() => handlePageChange(1)}
                >
                  First
                </button>
              </li>

              {/* Page Number Buttons */}
              {Array.from({ length: totalPages }).map((_, i) => (
                <li
                  key={i}
                  className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                >
                  <button
                    onClick={() => handlePageChange(i + 1)}
                    className="page-link rounded-2 border fw-semibold px-3"
                  >
                    {i + 1}
                  </button>
                </li>
              ))}

              {/* Last Page Button */}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link rounded-2 border px-3"
                  onClick={() => handlePageChange(totalPages)}
                >
                  Last
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </>
  );
};
